// @ts-nocheck
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Package, User, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface VideoVendor {
  id: string;
  business_name: string;
  description?: string;
  rating_average?: number;
  review_count?: number;
  user_id: string;
}

interface VideoPackage {
  id: string;
  vendor_id: string;
  package_type: 'silver' | 'gold' | 'platinum';
  name: string;
  price_cents: number;
  description: string;
  features: string[];
  is_active: boolean;
}

interface Event {
  id: string;
  name: string;
}

interface VideoPurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor: VideoVendor | null;
  selectedPackage: VideoPackage | null;
  event: Event | null;
  onSuccess: (orderId: string) => void;
}

const packageTypeColors = {
  silver: 'bg-gray-100 text-gray-800 border-gray-300',
  gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  platinum: 'bg-purple-100 text-purple-800 border-purple-300',
};

const VideoPurchaseModal: React.FC<VideoPurchaseModalProps> = ({
  open,
  onOpenChange,
  vendor,
  selectedPackage,
  event,
  onSuccess,
}) => {
  const { profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    player_name: '',
    player_email: '',
    player_phone: '',
    team_name: '',
    purchaser_phone: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!profile?.name) {
      newErrors.purchaser = 'Please ensure your profile has a name set';
    }
    if (!profile?.email) {
      newErrors.purchaser = 'Please ensure your profile has an email set';
    }
    if (!formData.player_name.trim()) {
      newErrors.player_name = 'Player name is required';
    }
    if (!formData.purchaser_phone.trim()) {
      newErrors.purchaser_phone = 'Purchaser phone number is required';
    }
    // Basic phone validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (formData.purchaser_phone.trim() && !phoneRegex.test(formData.purchaser_phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.purchaser_phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !vendor || !selectedPackage || !event || !profile) {
      return;
    }

    setIsLoading(true);

    try {
      // Create Stripe payment session via edge function
      const { data, error } = await invokeEdgeFunction('create-video-payment', {
        body: {
          vendorId: vendor.id,
          eventId: event.id,
          packageId: selectedPackage.id,
          purchaserName: profile.name,
          purchaserEmail: profile.email,
          purchaserPhone: formData.purchaser_phone,
          playerName: formData.player_name,
          playerEmail: formData.player_email || null,
          playerPhone: formData.player_phone || null,
          teamName: formData.team_name || null,
          totalAmountCents: selectedPackage.price_cents
        }
      });

      if (error) {
        console.error('Error creating video payment:', error);
        toast.error('Failed to initiate payment. Please try again.');
        return;
      }

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
        toast.success('Redirecting to payment...');
        onOpenChange(false);
        
        // Reset form
        setFormData({
          player_name: '',
          player_email: '',
          player_phone: '',
          team_name: '',
          purchaser_phone: '',
        });
      } else {
        throw new Error('No payment URL received');
      }
      
    } catch (error) {
      console.error('Unexpected error creating payment:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!vendor || !selectedPackage || !event) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Complete Video Purchase
          </DialogTitle>
          <DialogDescription>
            Fill out the information below to complete your video order
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{selectedPackage.package_type}</p>
                  <p className="text-sm text-muted-foreground">from {vendor.business_name}</p>
                  <Badge 
                    variant="outline" 
                    className={`text-xs font-semibold mt-1 ${packageTypeColors[selectedPackage.package_type]}`}
                  >
                    {selectedPackage.package_type.charAt(0).toUpperCase() + selectedPackage.package_type.slice(1)}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-primary">
                    {formatPrice(selectedPackage.price_cents)}
                  </p>
                  <p className="text-sm text-muted-foreground">Event: {event.name}</p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">{selectedPackage.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Purchase Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Purchaser Information - Display Only */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Purchaser Information
                </CardTitle>
                <CardDescription>
                  Order will be placed under your account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={profile?.name || ''}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={profile?.email || ''}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="purchaser_phone">Phone Number *</Label>
                  <Input
                    id="purchaser_phone"
                    type="tel"
                    value={formData.purchaser_phone}
                    onChange={(e) => handleInputChange('purchaser_phone', e.target.value)}
                    className={errors.purchaser_phone ? 'border-red-500' : ''}
                    placeholder="(555) 123-4567"
                  />
                  {errors.purchaser_phone && (
                    <p className="text-sm text-red-500">{errors.purchaser_phone}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Important - please add the phone number that you wish to be contacted on for this order - the vendor will follow up via SMS for questions about this order.
                  </p>
                </div>
                
                {errors.purchaser && (
                  <p className="text-sm text-red-500">{errors.purchaser}</p>
                )}
              </CardContent>
            </Card>

            {/* Player Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Player Information</CardTitle>
                <CardDescription>
                  Information about the athlete who will be featured in the video
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="player_name">Player Name *</Label>
                    <Input
                      id="player_name"
                      value={formData.player_name}
                      onChange={(e) => handleInputChange('player_name', e.target.value)}
                      className={errors.player_name ? 'border-red-500' : ''}
                    />
                    {errors.player_name && (
                      <p className="text-sm text-red-500">{errors.player_name}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="team_name">Team Name (optional)</Label>
                    <Input
                      id="team_name"
                      value={formData.team_name}
                      onChange={(e) => handleInputChange('team_name', e.target.value)}
                      placeholder="e.g., Lightning Bolts U16"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="player_email">Player Email (optional)</Label>
                    <Input
                      id="player_email"
                      type="email"
                      value={formData.player_email}
                      onChange={(e) => handleInputChange('player_email', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="player_phone">Player Phone (optional)</Label>
                    <Input
                      id="player_phone"
                      type="tel"
                      value={formData.player_phone}
                      onChange={(e) => handleInputChange('player_phone', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Complete Purchase {formatPrice(selectedPackage.price_cents)}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoPurchaseModal;